import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../components/layout/AppLayout';
import SLATimer from '../components/SLATimer';
import { useAuth } from '../context/AuthContext';
import { format } from 'date-fns';
import { Shield, AlertOctagon, PauseCircle, CheckCircle, Clock, RefreshCw } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function apiFetch(path) {
  const token = sessionStorage.getItem('itsm_token');
  return fetch(`${API_BASE}${path}`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json());
}

const PRIORITY_META = {
  critical: { label: 'P1 — Critical', color: '#dc2626', bg: '#fee2e2' },
  high:     { label: 'P2 — High',     color: '#ea580c', bg: '#ffedd5' },
  medium:   { label: 'P3 — Medium',   color: '#d97706', bg: '#fef3c7' },
  low:      { label: 'P4 — Low',      color: '#16a34a', bg: '#dcfce7' },
};

const STATUS_META = {
  open:        { label: 'Open',        color: '#2563eb', bg: '#dbeafe' },
  assigned:    { label: 'Assigned',    color: '#d97706', bg: '#fef3c7' },
  in_progress: { label: 'In Progress', color: 'var(--primary)', bg: 'var(--primary-100)' },
  pending:     { label: 'Pending',     color: '#ea580c', bg: '#ffedd5' },
};

export default function SLADashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter,  setFilter]  = useState('all'); // all | breached | paused | critical
  const [lastRefresh, setLastRefresh] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiFetch('/sla/dashboard');
      setTickets(Array.isArray(data) ? data : []);
      setLastRefresh(new Date());
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(load, 60_000);
    return () => clearInterval(iv);
  }, [load]);

  const filtered = tickets.filter(t => {
    if (filter === 'breached') return t.is_breached;
    if (filter === 'paused')   return t.is_paused;
    if (filter === 'critical') return t.priority === 'critical';
    return true;
  });

  const breachedCount = tickets.filter(t => t.is_breached).length;
  const pausedCount   = tickets.filter(t => t.is_paused).length;
  const criticalCount = tickets.filter(t => t.priority === 'critical').length;
  const onTrackCount  = tickets.filter(t => !t.is_breached && !t.is_paused).length;

  return (
    <AppLayout pageTitle="SLA Dashboard">
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-main)', margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Shield size={20} color="var(--primary)" /> SLA Dashboard
            </h2>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
              Business hours: 9 AM – 6 PM Mon–Fri · SLA pauses on checkout & leave
              {lastRefresh && ` · Updated ${format(lastRefresh, 'HH:mm:ss')}`}
            </p>
          </div>
          <button className="btn btn-outline btn-sm" onClick={load} style={{ gap: 6 }}>
            <RefreshCw size={13} /> Refresh
          </button>
        </div>

        {/* Summary cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
          {[
            { label: 'Total Active', value: tickets.length, icon: Clock,        color: '#2563eb',  bg: '#dbeafe', f: 'all' },
            { label: 'SLA Breached', value: breachedCount,  icon: AlertOctagon,  color: '#dc2626',  bg: '#fee2e2', f: 'breached' },
            { label: 'SLA Paused',   value: pausedCount,    icon: PauseCircle,   color: '#6b7280',  bg: '#f3f4f6', f: 'paused' },
            { label: 'On Track',     value: onTrackCount,   icon: CheckCircle,   color: '#16a34a',  bg: '#dcfce7', f: 'all' },
          ].map(c => (
            <div key={c.label} className="card"
              onClick={() => setFilter(c.f === filter ? 'all' : c.f)}
              style={{ padding: '14px 16px', cursor: 'pointer', border: filter === c.f ? `2px solid ${c.color}` : '1.5px solid var(--border)' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ width: 36, height: 36, borderRadius: 8, background: c.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <c.icon size={18} color={c.color} />
                </div>
                <span style={{ fontSize: 26, fontWeight: 800, color: 'var(--text-main)' }}>{c.value}</span>
              </div>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-main)', marginTop: 10 }}>{c.label}</div>
            </div>
          ))}
        </div>

        {/* Filter pills */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
          {[
            { key: 'all',      label: `All (${tickets.length})` },
            { key: 'breached', label: `Breached (${breachedCount})` },
            { key: 'paused',   label: `Paused (${pausedCount})` },
            { key: 'critical', label: `Critical (${criticalCount})` },
          ].map(f => (
            <button key={f.key} onClick={() => setFilter(f.key)} style={{
              padding: '5px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600,
              border: '1.5px solid ' + (filter === f.key ? 'var(--primary)' : 'var(--border)'),
              background: filter === f.key ? 'var(--primary-50, #ede9fe)' : 'var(--bg-card)',
              color: filter === f.key ? 'var(--primary)' : 'var(--text-sub)',
              cursor: 'pointer',
            }}>
              {f.label}
            </button>
          ))}
        </div>

        {/* Tickets table */}
        <div className="table-wrap">
          {loading ? (
            <div className="flex-center" style={{ height: 160 }}>
              <div className="spinner" style={{ width: 32, height: 32 }} />
            </div>
          ) : filtered.length === 0 ? (
            <div className="empty-state">
              <CheckCircle size={36} color="#16a34a" style={{ marginBottom: 10 }} />
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-muted)' }}>
                {filter === 'breached' ? 'No SLA breaches!' : filter === 'paused' ? 'No SLA pauses.' : 'No active tickets.'}
              </div>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Ticket</th>
                  <th>Title</th>
                  <th>Priority</th>
                  <th>Status</th>
                  <th>Assigned To</th>
                  <th>SLA Due</th>
                  <th>Remaining</th>
                  <th>Paused Time</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(t => {
                  const pm = PRIORITY_META[t.priority] || PRIORITY_META.medium;
                  const sm = STATUS_META[t.status] || STATUS_META.open;
                  return (
                    <tr key={t.id} onClick={() => navigate(`/tickets/${t.id}`)} style={{ cursor: 'pointer' }}>
                      <td>
                        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 700, color: 'var(--primary)', background: 'var(--primary-light)', padding: '2px 8px', borderRadius: 5 }}>
                          {t.ticket_number}
                        </span>
                      </td>
                      <td style={{ maxWidth: 220 }}>
                        <div style={{ fontWeight: 500, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.title}</div>
                      </td>
                      <td>
                        <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 6, background: pm.bg, color: pm.color }}>
                          {pm.label}
                        </span>
                      </td>
                      <td>
                        <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 6, background: sm.bg, color: sm.color }}>
                          {sm.label}
                        </span>
                      </td>
                      <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                        {t.assigned_to_name || <span style={{ fontStyle: 'italic' }}>Unassigned</span>}
                      </td>
                      <td style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                        {t.effective_due ? format(new Date(t.effective_due), 'dd MMM HH:mm') : '—'}
                        {t.total_paused_minutes > 0 && (
                          <div style={{ fontSize: 10, color: '#6b7280' }}>
                            +{Math.round(t.total_paused_minutes)}m paused
                          </div>
                        )}
                      </td>
                      <td onClick={e => e.stopPropagation()}>
                        <SLATimer ticketId={t.id} priority={t.priority} compact />
                      </td>
                      <td style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                        {t.total_paused_minutes > 0
                          ? `${Math.floor(t.total_paused_minutes / 60)}h ${Math.round(t.total_paused_minutes % 60)}m`
                          : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

      </div>
    </AppLayout>
  );
}
